import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { morph } from "@theme/morph";
import { shippingCalculatorCountryConfig } from "@theme/utilities";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that calculates shipping rates for products.
 *
 * @typedef {Object} ShippingCalculatorComponentRefs
 * @property {HTMLFormElement} shippingForm - The shipping calculation form.
 * @property {HTMLDetailsElement} details - The details element for the calculator.
 * @property {HTMLElement} results - The results container.
 * @property {HTMLElement} loading - The loading indicator.
 * @property {HTMLElement} error - The error container.
 * @property {HTMLElement} shippingOptions - The shipping options container.
 * @property {HTMLButtonElement} calculateButton - The calculate button.
 * @property {HTMLElement} errorMessage - The error message element.
 * @property {HTMLSelectElement} countrySelect - The country select dropdown.
 * @property {HTMLInputElement} postalInput - The postal code input.
 * @property {HTMLInputElement} provinceInput - The province/state input.
 * @property {HTMLElement} locationNotice - The location notice in the summary.
 * @property {HTMLElement} locationTaxNotice - The location tax notice in the summary.
 * @property {TextComponent} shippingCost - The shipping cost text component.
 * @property {TextComponent} totalCost - The total cost text component.
 * @property {HTMLButtonElement} clearButton - The clear button.
 *
 * @typedef {Object} CustomerAddress
 * @property {string} country - The country code.
 * @property {string} zip - The postal/zip code.
 * @property {string} province - The province/state.
 */

/**
 * @extends {Component<ShippingCalculatorComponentRefs>}
 */
class ShippingCalculator extends Component {
  requiredRefs = [
    "shippingForm",
    "details",
    "results",
    "loading",
    "error",
    "shippingOptions",
    "calculateButton",
    "errorMessage",
    "countrySelect",
    "postalInput",
    "provinceInput",
    "locationNotice",
    "shippingCost",
    "totalCost",
    "clearButton",
  ];

  /** @type {AbortController | null} */
  #activeFetch = null;

  countryConfig = shippingCalculatorCountryConfig;

  /** @type {CustomerAddress | null} */
  #shippingAddress = null;

  /** @static @constant {string} The key used to store the delivery address in local storage */
  #STORAGE_KEY = "deliveryAddress";

  async connectedCallback() {
    super.connectedCallback();

    const { shippingForm, countrySelect } = this.refs;

    shippingForm.addEventListener("submit", this.handleSubmit);
    countrySelect.addEventListener("change", this.handleCountryChange);

    document.addEventListener(
      ThemeEvents.itemQuantityUpdate,
      this.#setCalculateLoading
    );

    document.addEventListener(ThemeEvents.cartUpdate, this.#updateShippingCost);
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError);

    this.refs.clearButton.addEventListener("click", this.#clearStoredAddress);

    await this.#syncStoredAddress();

    if (countrySelect.value) {
      this.#updateFieldLabels(countrySelect.value);
    }

    if (this.#hasCustomerData() || this.#shippingAddress) {
      setTimeout(() => {
        this.handleSubmit(new Event("submit"));
      }, 500);
    }
  }

  /** @type {(show: boolean) => void} */
  #toggleCartTotalBeforeShipping = (show) => {
    /** @type {HTMLElement | null} */
    const cartOriginalTotal = document.querySelector(
      '[ref="cartOriginalTotal"]'
    );
    if (cartOriginalTotal) {
      cartOriginalTotal.style.display = show ? "" : "none";
    }
  };

  #setCalculateLoading = () => {
    this.refs?.shippingCost?.shimmer();
    this.refs?.totalCost?.shimmer();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#updateShippingCost
    );
    document.removeEventListener(
      ThemeEvents.itemQuantityUpdate,
      this.#setCalculateLoading
    );
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Synchronizes the stored address from session storage to the form fields.
   * @returns {Promise<void>}
   */
  async #syncStoredAddress() {
    const storedAddress = this.#getStoredAddress();
    if (storedAddress) {
      this.refs.countrySelect.value = storedAddress.country;
      this.refs.postalInput.value = storedAddress.zip;
      this.refs.provinceInput.value = storedAddress.province;
      this.#shippingAddress = storedAddress;
    }
  }

  /**
   * Stores the selected address in session storage.
   * @param {CustomerAddress} address - The address to store.
   */
  #storeSelectedAddress(address) {
    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(address));
  }

  /**
   * Retrieves the stored address from session storage.
   * @returns {CustomerAddress | null} The stored address or null if not found.
   */
  #getStoredAddress() {
    const stored = localStorage.getItem(this.#STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  }

  /**
   * Clears the stored address from session storage and resets the form fields.
   */
  #clearStoredAddress = () => {
    const { postalInput, provinceInput } = this.refs;
    const storedAddress = this.#getStoredAddress();
    postalInput.value = "";
    provinceInput.value = "";

    if (!storedAddress) return;
    localStorage.removeItem(this.#STORAGE_KEY);
    this.#shippingAddress = null;
  };

  #hasCustomerData() {
    return this.dataset.customerCountry && this.dataset.customerZip;
  }

  /**
   * Handles country selection change.
   * @param {Event} event - The change event.
   */
  handleCountryChange = (event) => {
    const { countrySelect, postalInput, provinceInput } = this.refs;

    if (!(event.target instanceof HTMLSelectElement)) return;

    const country = event.target.value;
    this.#updateFieldLabels(country);
    this.#hideAll();
    this.#updateLocationNotice(); // Reset the summary

    if (
      !this.#hasCustomerData() ||
      countrySelect.value !== this.dataset.customerCountry
    ) {
      if (postalInput) {
        postalInput.value = "";
      }
      if (provinceInput) {
        provinceInput.value = "";
      }
    }
  };

  /**
   * Updates field labels and visibility based on selected country.
   * @param {string} countryCode - The country code.
   */
  #updateFieldLabels(countryCode) {
    const { postalInput, provinceInput } = this.refs;

    const config = /** @type {any} */ (this.countryConfig)[countryCode] || {
      postal: { label: "Postal code", placeholder: "Enter postal code" },
      province: {
        label: "State/Province",
        placeholder: "Enter state or province",
        required: false,
      },
    };

    if (postalInput) {
      postalInput.placeholder = config.postal.placeholder;
    }

    // Show/hide province field based on whether it's required
    if (provinceInput) {
      const provinceField = provinceInput.closest(
        ".shipping-calculator__field"
      );

      if (config.province && config.province.required) {
        provinceField?.classList.remove("shipping-calculator__hidden");
        provinceInput.placeholder = config.province.placeholder;
        provinceInput.required = true;
      } else {
        provinceField?.classList.add("shipping-calculator__hidden");
        provinceInput.required = false;
        provinceInput.value = "";
      }
    }
  }

  /**
   * Handles form submission to calculate shipping rates.
   * @param {Event | SubmitEvent} event - The submit event.
   */
  handleSubmit = async (event) => {
    const { shippingForm } = this.refs;

    event.preventDefault();

    const formData = new FormData(shippingForm);
    const country = formData.get("country");
    const zip = formData.get("zip");
    const province = formData.get("province");

    if (
      !country ||
      !zip ||
      typeof country !== "string" ||
      typeof zip !== "string"
    ) {
      this.#showError("Please fill in all required fields.");
      return;
    }

    this.#shippingAddress = {
      country,
      zip,
      province: typeof province === "string" ? province : "",
    };

    this.#storeSelectedAddress(this.#shippingAddress);

    this.#showLoading();

    await this.#updateShippingCost();
  };

  #handleCartError = () => {
    this.#activeFetch?.abort();
  };

  #updateShippingCost = async () => {
    if (this.#shippingAddress) {
      const { country, zip, province } = this.#shippingAddress;

      const abortController = this.#createAbortController();

      try {
        const response = await this.#fetchShippingRates(
          country,
          zip,
          typeof province === "string" ? province : "",
          abortController.signal
        );

        if (response.shipping_rates && response.shipping_rates.length > 0) {
          this.refs.details.open = false;
          this.#hideAll();

          await this.#updateLocationNotice(zip, response.shipping_rates);
        } else {
          this.#showError("No shipping rates available for this location.");
        }
      } catch (error) {
        if (error instanceof Error && error.name !== "AbortError") {
          console.error("Shipping calculation error:", error);
          this.#showError(
            error.message ||
              "Unable to calculate shipping rates. Please try again."
          );
        }
      } finally {
        if (this.#activeFetch === abortController) {
          this.#activeFetch = null;
        }
      }
    }
  };

  /**
   * Fetches the current cart state.
   * @param {AbortSignal} signal - The abort signal.
   * @returns {Promise<{items: Array<{id: number, variant_id?: number, quantity: number}>}>} The cart data.
   */
  async #fetchCart(signal) {
    const response = await fetch("/cart.js", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Fetches shipping rates from the server for all cart items.
   * @param {string} country - The country code.
   * @param {string} zip - The postal/zip code.
   * @param {string} province - The province/state.
   * @param {AbortSignal} signal - The abort signal.
   * @returns {Promise<{shipping_rates: Array<{name: string, price: string, delivery_range?: string}>}>} The shipping rates response.
   */
  async #fetchShippingRates(country, zip, province, signal) {
    const cart = await this.#fetchCart(signal);

    if (!cart.items || cart.items.length === 0) {
      throw new Error("Cart is empty");
    }

    const params = new URLSearchParams({
      "shipping_address[country]": country,
      "shipping_address[zip]": zip,
      "shipping_address[province]": province || "",
    });

    cart.items.forEach((item) => {
      params.append("items[][id]", String(item.variant_id || item.id));
      params.append("items[][quantity]", String(item.quantity));
    });

    const response = await fetch(`/cart/shipping_rates.json?${params}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      signal,
    });

    if (!response.ok) {
      let errorMessage = "Unable to calculate shipping rates";
      try {
        const errorData = await response.json();
        if (errorData.zip) {
          errorMessage =
            "Please enter a valid postal code for the selected location";
        } else if (errorData.province) {
          errorMessage = "Please select a valid state/province";
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        if (response.status === 422) {
          errorMessage = "Shipping rates are not available for this location";
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  }

  /**
   * Updates the location notice in the summary with shipping cost and total.
   * @param {string} [zip] - The postal/zip code.
   * @param {Array<{name: string, price: string}>} [rates] - The shipping rates.
   */
  async #updateLocationNotice(zip, rates) {
    const { locationNotice, locationTaxNotice, shippingCost, totalCost } =
      this.refs;

    if (!locationNotice) return;

    if (locationTaxNotice) {
      locationTaxNotice.dataset.country = this.#shippingAddress?.country || "";
    }

    // If no rates provided, show basic location or reset
    if (!rates || rates.length === 0) {
      const tempContainer = document.createElement("div");

      tempContainer.innerHTML = `<span>Add your address to see total<text-component ref="shippingCost"></text-component><text-component ref="totalCost"></text-component></span>`;

      this.#toggleCartTotalBeforeShipping(true);

      morph(locationNotice, tempContainer);
      return;
    }

    // Get cart to calculate total
    const cart = await fetch("/cart.js").then((res) => res.json());
    const cartSubtotal = cart.total_price / 100;

    // Find the cheapest rate
    const cheapestRate = rates.reduce((min, rate) => {
      const currentPrice = parseFloat(rate.price);
      const minPrice = parseFloat(min.price);
      return currentPrice < minPrice ? rate : min;
    });

    const shippingCostAmount = Math.ceil(parseFloat(cheapestRate.price));
    const total = cartSubtotal + shippingCostAmount;
    const isFree = cheapestRate.price === "0.00";

    const displayZip = zip || this.dataset.customerZip;
    const formattedShipping = isFree
      ? "Free"
      : this.#formatMoney(cheapestRate.price);
    const formattedTotal = this.#formatMoney(total.toFixed(2));

    if (shippingCostAmount !== null || isFree) {
      this.#toggleCartTotalBeforeShipping(false);
    }

    // If elements already exist, just update them directly (no morph)
    if (shippingCost.textContent !== "" && totalCost.textContent !== "") {
      shippingCost.setAttribute("value", formattedShipping);
      shippingCost.textContent = formattedShipping;
      shippingCost.removeAttribute("shimmer");

      totalCost.setAttribute("value", formattedTotal);
      totalCost.textContent = formattedTotal;
      totalCost.removeAttribute("shimmer");

      // Update zip code
      const zipElement = locationNotice.querySelector(".link");
      if (zipElement) {
        zipElement.textContent = displayZip?.toUpperCase() || "";
      }
    } else {
      // First time - create structure with morph
      const tempContainer = document.createElement("div");
      tempContainer.innerHTML = `
      <span><span>Postage to <span class="link">${displayZip?.toUpperCase()}${
        this.#shippingAddress?.province
          ? `, ${this.#shippingAddress?.province}`
          : ""
      }</span></span> <text-component ref="shippingCost" value="${formattedShipping}">${formattedShipping}</text-component></span>
      <span class="shipping-calculator__location-total"><span>Tally Ho!</span> <text-component ref="totalCost" value="${formattedTotal}">${formattedTotal}</text-component></span>
    `;
      morph(locationNotice, tempContainer);
    }
  }

  #showLoading() {
    const { calculateButton, loading } = this.refs;

    this.#hideAll();
    calculateButton.disabled = true;
    calculateButton.innerHTML =
      "<span class='shipping-calculator__spinner'></span> Calculating...";
    calculateButton.classList.add("shipping-calculator__button--loading");
    // loading.classList.remove("shipping-calculator__hidden");
  }

  /**
   * Shows an error message.
   * @param {string} message - The error message to display.
   */
  #showError(message) {
    const { errorMessage, error } = this.refs;

    this.#hideAll();
    errorMessage.textContent = message;
    error.classList.remove("shipping-calculator__hidden");
  }

  #hideAll() {
    const { calculateButton, results, loading, error } = this.refs;

    calculateButton.disabled = false;
    calculateButton.innerText = "Calculate postage";
    calculateButton.classList.remove("shipping-calculator__button--loading");
    results.classList.add("shipping-calculator__hidden");
    // loading.classList.add("shipping-calculator__hidden");
    error.classList.add("shipping-calculator__hidden");
  }

  /**
   * Formats a price value as currency using Shopify's money formatting.
   * @param {string} cents - The price in cents as a string.
   * @returns {string} The formatted currency string.
   */
  #formatMoney(cents) {
    const amount = Math.ceil(parseFloat(cents));

    // Use Shopify's currency if available, otherwise fall back to generic formatting
    if (window.Shopify && window.Shopify.currency) {
      const formatter = new Intl.NumberFormat(
        window.Shopify.locale || "en-US",
        {
          style: "currency",
          currency: window.Shopify.currency.active || "USD",
        }
      );
      return formatter.format(amount);
    }

    // Fallback: generic formatting
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  }
}

if (!customElements.get("shipping-calculator")) {
  customElements.define("shipping-calculator", ShippingCalculator);
}
